import React, { useMemo } from 'react';
import { Billboard, Text } from '@react-three/drei';
import { useWorldStore } from '../state/worldStore';
import { SEATED_EYE } from '../services/seats';
import CameraBasisProbe from './CameraBasisProbe';

// PlaceableBadges — the in-scene half of the Placeables tool: a numbered tag
// floating at every placed thing in the room, tappable to select. The numbers
// are computed the SAME way the panel computes them (kind order, then slot
// order), so "#3" in the room and "#3" in the panel are always the same object.
//
// Badges billboard — they are UI, not set dressing, and a tag you have to walk
// around to read is a tag that fails on a phone.

// Must stay in step with PlaceablesPanel's KINDS — a kind missing here places
// fine and then shows no badge, which reads as "the seat didn't save".
const KIND_ORDER = ['video_panel', 'plaque', 'result_card', 'card', 'seat'];

// ── A SEAT HAS TO BE DRAWN, because a seat has no object ─────────────────────
//
// Everything else placed in a room IS something you can see — a panel, a sleeve,
// a plaque — so a numbered tag floating over it is enough to aim with. A seat is
// a bare coordinate. With only a badge above it there is nothing to line up
// against a cushion, and (the badges draw through walls, on purpose) a seat on
// the couch and a seat inside the shelf behind it look identical.
//
// So: a ring on the floor where the seat is, a wedge for which way the sitter
// faces, and a ghost head at seated eye height — enough to tell "on the cushion"
// from "in the coffee table" at a glance. Admin-only; this whole file is.
function SeatGhost({ pos, rotY = 0, size = 0.4, active }) {
  const r = Math.max(0.1, (size || 0.4) / 2);
  const c = active ? '#ffd27d' : '#ffb25e';
  return (
    <group position={pos} rotation={[0, rotY, 0]}>
      {/* The floor mark. Flat on the ground at the seat's own y, so a seat left
          at eye height reads instantly as a hoop in the air. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0]}>
        <ringGeometry args={[r * 0.82, r, 40]} />
        <meshBasicMaterial color={c} transparent opacity={active ? 0.95 : 0.6} depthTest={false} side={2} />
      </mesh>
      {/* WHICH WAY THEY LOOK. Local +z is the facing direction — the same
          convention the stored rotY is read back with. */}
      <mesh position={[0, 0.005, r + 0.13]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[r * 0.42, r * 0.6, 3]} />
        <meshBasicMaterial color={c} transparent opacity={active ? 0.95 : 0.55} depthTest={false} />
      </mesh>
      {/* Where the head ends up. The one measurement that decides whether a seat
          is on the cushion or through the back of the couch. */}
      <mesh position={[0, SEATED_EYE, 0]}>
        <sphereGeometry args={[0.075, 14, 10]} />
        <meshBasicMaterial color={c} transparent opacity={active ? 0.6 : 0.32} depthTest={false} />
      </mesh>
    </group>
  );
}

export default function PlaceableBadges() {
  const on = useWorldStore((s) => s.adminTool) === 'placeables';
  const interior = useWorldStore((s) => s.interior);
  const objects = useWorldStore((s) => s.worldObjects);
  const sel = useWorldStore((s) => s.worldObjectSel);
  const setSel = useWorldStore((s) => s.setWorldObjectSel);

  const items = useMemo(() => {
    if (!on || !interior) return [];
    let n = 0;
    return KIND_ORDER.flatMap((kind) => objects
      .filter((o) => o.zone_id === interior && o.kind === kind)
      .sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0))
      .map((o) => ({ ...o, badge: ++n })));
  }, [on, interior, objects]);

  if (!on || !interior) return null;

  return (
    <group>
      {/* The panel's camera-relative nudges and "+ add ahead of me" read
          camBasis, which only stays current while a probe is in the scene —
          mounted here even when the room is empty, because placing the FIRST
          thing is exactly when the camera position matters. */}
      <CameraBasisProbe />
      {items.filter((o) => o.kind === 'seat').map((o) => (
        <SeatGhost key={`ghost-${o.object_id}`} pos={o.pos} rotY={o.rotY} size={o.size} active={o.object_id === sel} />
      ))}
      {items.map((o) => {
        const active = o.object_id === sel;
        // Float the tag just above the thing it names; a video panel's size is
        // its height, a plaque's its edge — half of either clears the top.
        // A seat is stored at the FLOOR, so half its size would bury the tag in
        // the cushion — it goes where the sitter's head is instead.
        const y = o.kind === 'seat'
          ? o.pos[1] + SEATED_EYE + 0.26
          : o.pos[1] + (o.size || 1) / 2 + 0.16;
        return (
          <Billboard key={o.object_id} position={[o.pos[0], y, o.pos[2]]}>
            <mesh
              onClick={(e) => { e.stopPropagation(); setSel(o.object_id); }}
              onPointerOver={() => { document.body.style.cursor = 'pointer'; }}
              onPointerOut={() => { document.body.style.cursor = ''; }}
            >
              <circleGeometry args={[0.14, 24]} />
              <meshBasicMaterial color={active ? '#ffb25e' : '#1c1206'} transparent opacity={0.92} depthTest={false} />
            </mesh>
            <mesh>
              <ringGeometry args={[0.13, 0.15, 24]} />
              <meshBasicMaterial color="#ffb25e" transparent opacity={active ? 1 : 0.7} depthTest={false} />
            </mesh>
            <Text position={[0, 0, 0.01]} fontSize={0.14} color={active ? '#1c1206' : '#ffd27d'} anchorX="center" anchorY="middle" renderOrder={10}>
              {String(o.badge)}
            </Text>
          </Billboard>
        );
      })}
    </group>
  );
}
